export const UPDATE_ROUTE = 'UPDATE_ROUTE';
export const UPDATE_PAGE = 'UPDATE_PAGE';
export const RESET_ROUTES = 'RESET_ROUTES';
export const SET_ALL_ROUTES = 'SET_ALL_ROUTES';
export const START_SCROLL = 'START_SCROLL';
export const END_SCROLL = 'END_SCROLL';

import { READ } from 'src/consts';
import type {
  AnalyticsLinkEvent,
  AnalyticsActionOrigin,
  AnalyticsCurrentPageSectionState,
  AnalyticsCurrentPageSubsection,
} from 'src/modules/analytics/types';
import { parseUrl } from 'src/modules/utils';
import type { Card, Mode, Page, Action, Slug } from 'src/types';

import {
  APPROACHABILITY,
  AVAILABILITY,
  CAREER_HISTORY,
  CLASSIFICATION,
  CREATE_RESUME,
  CREATE_RESUME_PAGE,
  CREATE_RESUME_PAGE_URL,
  EDUCATION,
  FINISH_RESUME,
  LANGUAGES,
  LICENCES,
  NEXT_ROLE,
  PERSONAL_DETAILS,
  PREFERRED_LOCATIONS,
  PROFILE_INSIGHTS,
  PROFILE_PAGE,
  PROFILE_VISIBILITY,
  PROFILE_VISIBILITY_V2,
  REPORT_PROFILE,
  RESUME,
  RIGHT_TO_WORK,
  SALARY,
  SHAREABLE_PROFILE,
  SHAREABLE_PROFILE_PAGE,
  SHAREABLE_PROFILE_PAGE_URL_REGEX,
  SKILLS,
  START_RESUME,
  SUGGESTIONS,
  SUMMARY,
  TIPS,
  VERIFICATIONS,
  VERIFIED_IDENTITY,
  WORKTYPES,
} from './index';

export interface ProfilePageSettings {
  card?: Slug;
  field?: string;
  mode?: Mode;
  item?: string;
  previousPage?: string;
  returnUrl?: string;
  formData?: string;
  profileDataSource?: string;
  features?: string;
}

type Params = Record<string, unknown> & {
  id?: string;
};

type CardsState = Record<
  Card,
  {
    mode: Mode;
    params: Params;
  }
>;

export type State = {
  isScrolling: boolean;
  queryStringRoute: ProfilePageSettings;
  page: Page;
  shareableProfileSlug: string;
  previousPage?: string;
  latestRoute: {
    card?: Card;
    mode?: Mode;
    params?: Params;
  };
} & CardsState;

const queryStringParams: ProfilePageSettings =
  typeof window !== 'undefined' ? parseUrl(window.location) : {};
const { card, field, item, mode: initialMode } = queryStringParams;

export const getPage = () => {
  const path =
    typeof window !== 'undefined' ? decodeURI(window.location.pathname) : '';
  if (path.includes(CREATE_RESUME_PAGE_URL)) {
    return CREATE_RESUME_PAGE;
  }

  if (path.match(SHAREABLE_PROFILE_PAGE_URL_REGEX)) {
    return SHAREABLE_PROFILE_PAGE;
  }

  return PROFILE_PAGE;
};

export const getShareableProfileSlug = () => {
  if (getPage() === SHAREABLE_PROFILE_PAGE) {
    const path = typeof window !== 'undefined' ? window.location.pathname : '';
    const matches = path.match(/\/profile\/(.*)$/);
    return matches ? matches[1] : '';
  }
  return '';
};

export const initialState: State = {
  [APPROACHABILITY]: { mode: READ, params: {} },
  [AVAILABILITY]: { mode: READ, params: {} },
  [CAREER_HISTORY]: { mode: READ, params: {} },
  [CLASSIFICATION]: { mode: READ, params: {} },
  [CREATE_RESUME]: { mode: READ, params: {} },
  [EDUCATION]: { mode: READ, params: {} },
  [FINISH_RESUME]: { mode: READ, params: {} },
  [LANGUAGES]: { mode: READ, params: {} },
  [LICENCES]: { mode: READ, params: {} },
  [NEXT_ROLE]: { mode: READ, params: {} },
  [PERSONAL_DETAILS]: { mode: READ, params: {} },
  [PREFERRED_LOCATIONS]: { mode: READ, params: {} },
  [PROFILE_INSIGHTS]: { mode: READ, params: {} },
  [PROFILE_VISIBILITY]: { mode: READ, params: {} },
  [PROFILE_VISIBILITY_V2]: { mode: READ, params: {} },
  [REPORT_PROFILE]: { mode: READ, params: {} },
  [RESUME]: { mode: READ, params: {} },
  [RIGHT_TO_WORK]: { mode: READ, params: {} },
  [SALARY]: { mode: READ, params: {} },
  [SHAREABLE_PROFILE]: { mode: READ, params: {} },
  [SKILLS]: { mode: READ, params: {} },
  [START_RESUME]: { mode: READ, params: {} },
  [SUGGESTIONS]: { mode: READ, params: {} },
  [SUMMARY]: { mode: READ, params: {} },
  [TIPS]: { mode: READ, params: {} },
  [VERIFICATIONS]: { mode: READ, params: {} },
  [VERIFIED_IDENTITY]: { mode: READ, params: {} },
  [WORKTYPES]: { mode: READ, params: {} },
  latestRoute: {},
  previousPage: queryStringParams?.previousPage,
  queryStringRoute: {
    card,
    field,
    item,
    mode: initialMode?.toUpperCase() as Mode | undefined,
  },
  isScrolling: false,
  page: getPage(),
  shareableProfileSlug: getShareableProfileSlug(),
};

export interface UpdatePageParams {
  page: Page;
  params?: Params;
  analytics?: Record<string, unknown>;
}

export interface UpdateRouteParams {
  to: Card;
  mode: Mode;
  params?: Params;
  analytics?: {
    origin?: AnalyticsActionOrigin;
    actionName?: AnalyticsLinkEvent;
    currentPageSectionState?: AnalyticsCurrentPageSectionState;
    currentPageSubsection?: AnalyticsCurrentPageSubsection;
  };
}

export type RouterAction =
  | (Action<typeof UPDATE_ROUTE> & {
      payload: UpdateRouteParams;
    })
  | (Action<typeof UPDATE_PAGE> & {
      payload: UpdatePageParams;
    })
  | (Action<typeof SET_ALL_ROUTES> & {
      payload: State;
    })
  | Action<typeof RESET_ROUTES>
  | Action<typeof START_SCROLL>
  | Action<typeof END_SCROLL>;

export const routerReducer = (
  state: State = initialState,
  action: RouterAction,
): State => {
  switch (action.type) {
    case UPDATE_ROUTE: {
      const { to, mode, params } = action.payload;

      // The sequence of the spread here is important
      return {
        // #1
        // - Spread the existing state
        ...state,

        // #2
        // - If there is latestRoute card previously
        // - reset the params to {} to avoid the scrolling bug in UI
        ...(state.latestRoute?.card
          ? {
              [state.latestRoute.card]: {
                ...state[state.latestRoute.card],
                params: {},
              },
            }
          : {}),

        // #3
        // - Update latest route info to the `[card]` key
        [to]: {
          mode,
          params,
        },

        // #4
        // - Update latest route info to the `latestRoute`
        latestRoute: {
          card: to,
          mode,
          params,
        },
      };
    }

    case UPDATE_PAGE: {
      const { page } = action.payload;

      return {
        ...state,
        previousPage: state.page,
        page,
      };
    }

    case SET_ALL_ROUTES: {
      return action.payload;
    }

    case RESET_ROUTES: {
      return initialState;
    }

    case START_SCROLL: {
      return { ...state, isScrolling: true };
    }

    case END_SCROLL: {
      return { ...state, isScrolling: false };
    }

    default:
      return state;
  }
};
